import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card.tsx'
import { Button } from '@/app/common/components/button/Button.tsx'
import { Input } from '@/app/common/components/input/Input.tsx'
import Label from '@/app/common/components/Label'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import { Switch } from '@/app/common/components/Switch'
import { PartialPurseData } from '../types'
import { ProgramPurseResource } from '@/queries/program_purses/schemas'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import {
  DistributionModifierPayload,
  useUpdateDistributionModifiers,
} from '@/queries/purse_configs/useUpdateDistributionModifier.ts'
import { useQueryClient } from '@tanstack/react-query'
import { PURSE_KEYS } from '@/queries/program_purses/keys'
import { SubmitButton } from '@/app/common/components/SubmitButton'

const specialNeedsModifierSchema = z.object({
  modifierAmounts: z.record(
    z.string(),
    z.coerce.number().min(0, 'Amount must be greater than or equal to 0')
  ),
  isActive: z.boolean(),
})

type SpecialNeedsModifierFormData = z.infer<typeof specialNeedsModifierSchema>

type Props = {
  partialPurseData: PartialPurseData
  distributionModifiers: ProgramPurseResource['distributions_rules']['distribution_modifiers']
  onBack: () => void
}

export default function SpecialNeedsModifierView({
  partialPurseData,
  distributionModifiers,
  onBack,
}: Props) {
  const queryClient = useQueryClient()
  const updateModifiersMutation = useUpdateDistributionModifiers()
  const { toast } = useToast()

  const specialEducationModifiers = distributionModifiers.filter(
    (modifier) => modifier.modifier_code === 'special_education'
  )

  const defaultValues: SpecialNeedsModifierFormData = {
    modifierAmounts: specialEducationModifiers.reduce(
      (acc, modifier) => {
        acc[modifier.id] = modifier.base_amount ?? 0
        return acc
      },
      {} as Record<string, number>
    ),
    isActive: specialEducationModifiers.some((modifier) => modifier.is_active),
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<SpecialNeedsModifierFormData>({
    resolver: zodResolver(specialNeedsModifierSchema),
    values: defaultValues,
  })

  const isSubmitting = updateModifiersMutation.status === 'pending'

  const onSubmit = async (data: SpecialNeedsModifierFormData) => {
    const payload: DistributionModifierPayload = specialEducationModifiers.map(
      (m) => ({
        id: m.id,
        base_amount: data.modifierAmounts[m.id],
        modifier_type_id: m.modifier_type_id,
        grade_level_id: m.grade_level_id,
        is_active: data.isActive,
      })
    )

    updateModifiersMutation.mutate(
      {
        params: {
          programId: partialPurseData.programId,
          purseId: partialPurseData.id,
        },
        payload,
      },
      {
        onSuccess: () => {
          reset(data)
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSE_DETAILS(
              partialPurseData.id.toString(),
              partialPurseData.programId
            ),
          })
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSES(partialPurseData.programId),
          })
          toast({
            title: 'Success',
            description: 'Special needs funding amounts updated successfully',
          })
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description:
              error.message || 'Failed to update special needs funding amounts',
          })
        },
      }
    )
  }

  return (
    <div className="w-full px-3 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <form id="special-needs-funding-form" onSubmit={handleSubmit(onSubmit)}>
          <Card>
            <CardHeader>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  Special Needs Additional Funding
                </h3>
                <div className="mt-1 text-sm text-gray-700">
                  <p>
                    Configure supplemental award amounts for students with
                    documented special needs.
                    <br />
                  </p>
                  <p className="mt-2">
                    <b>
                      This modifier adds extra funding on top of the base
                      scholarship amount{' '}
                    </b>
                    to help cover additional educational expenses and support
                    services.
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-1.5">
                  <Label htmlFor="special-education-is-active" weight="medium">
                    Modifier Status
                  </Label>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-600">
                          {field.value ? 'Active' : 'Inactive'}
                        </span>
                        <Switch
                          id="special-education-is-active"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </div>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                  {specialEducationModifiers.map((modifier) => (
                    <div key={modifier.id} className="space-y-1.5">
                      <Label
                        htmlFor={`modifier-${modifier.id}`}
                        weight="medium"
                      >
                        {modifier.grade_level_name}
                      </Label>
                      <div className="relative">
                        <span
                          aria-hidden
                          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500"
                        >
                          $
                        </span>
                        <Controller
                          name={`modifierAmounts.${modifier.id}`}
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id={`modifier-${modifier.id}`}
                              type="number"
                              inputMode="decimal"
                              min={0}
                              step="0.01"
                              className="pl-6"
                              placeholder="0"
                              onWheel={(e) => e.currentTarget.blur()}
                            />
                          )}
                        />
                        {errors.modifierAmounts?.[modifier.id] && (
                          <FormErrorMessage
                            message={
                              errors.modifierAmounts[modifier.id]?.message
                            }
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex justify-start gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onBack}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
                <SubmitButton
                  status={updateModifiersMutation.status}
                  form="special-needs-funding-form"
                  variant="primary"
                  size="sm"
                  className="w-auto"
                  buttonTxt="Save Changes"
                  submittingText="Saving..."
                  forceDisabled={!isDirty}
                />
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  )
}
